import React from 'react';
import { BUSINESS_STAGES, Stage } from '@orbit/shared/constants';
import clsx from 'clsx';
import { useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { useTaskProgress } from '../hooks/useTaskProgress';
import { ArrowRightLong } from '@orbit/ui';

interface StageProgressBarProps {
  highlightStage?: Stage;
  className?: string;
}

const StageProgressBar: React.FC<StageProgressBarProps> = ({ highlightStage, className = '' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { stageProgress, loading } = useTaskProgress();

  const getStageTooltipContent = (stage: Stage) => {
    if (loading) {
      return 'Loading progress...';
    }

    const progress = (stageProgress as Record<Stage, any>)[stage];
    if (!progress) {
      return `${stage}: No tasks found`;
    }

    const { totalTasks, completedTasks, completionPercentage } = progress;

    if (totalTasks === 0) {
      return 'No tasks available';
    }

    return `${completedTasks}/${totalTasks} tasks completed (${completionPercentage}%)`;
  };

  return (
    <div className={clsx('flex items-center justify-center w-full', className)}>
      <div
        className={clsx('flex items-center gap-4', isMobile ? 'grid grid-cols-2 gap-4' : 'flex')}
      >
        {BUSINESS_STAGES.map((stage, index) => {
          const isHighlighted = highlightStage === stage.name;
          const isLast = index === BUSINESS_STAGES.length - 1;

          const progress = loading ? null : (stageProgress as Record<Stage, any>)[stage.name];
          const percentage = progress ? progress.completionPercentage : 0;
          const isCompleted = percentage === 100;

          return (
            <div key={stage.name} className="flex items-center">
              {/* Stage Card */}
              <Tooltip
                title={getStageTooltipContent(stage.name)}
                arrow
                placement="top"
                enterDelay={500}
                leaveDelay={0}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={clsx(
                      'relative w-24 h-20 rounded-lg border-2 transition-all duration-300 flex flex-col items-center justify-center',
                      isHighlighted
                        ? 'border-primary bg-primary/10'
                        : isCompleted
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-gray-600 bg-gray-800/50',
                    )}
                  >
                    {/* Progress Bar */}
                    <div className="w-16 h-2 bg-gray-700 rounded-full mb-3">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all duration-700 ease-out',
                          isHighlighted
                            ? 'bg-primary'
                            : isCompleted
                              ? 'bg-green-500'
                              : 'bg-blue-500',
                        )}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>

                    {/* Percentage */}
                    <div className="text-base font-bold">{loading ? '...' : `${percentage}%`}</div>

                    {/* Completion Checkmark */}
                    {isCompleted && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-background-dark shadow-lg">
                        <svg
                          className="w-3.5 h-3.5 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Stage Name */}
                  <div className="text-sm font-medium text-center mt-2 text-gray-300">
                    {isMobile ? stage.name.slice(0, 6) : stage.name}
                  </div>
                </div>
              </Tooltip>

              {/* Connecting Arrow - Only show on desktop */}
              {!isLast && !isMobile && (
                <div className="flex items-center">
                  <ArrowRightLong className="w-20 h-6 text-gray-600" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StageProgressBar;
